import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Engineering as EngineeringIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  CreditCard as CardIcon,
  Videocam as VideocamIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../services/api';
import gsfService from '../services/gsfService';
import GSFInlineSearch from './GSFInlineSearch';

const AdminJobRequestModal = ({ open, onClose, jobRequest, onComplete, onDiscard }) => {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  // GSF Inline Search state - tracks which item index has search expanded
  const [gsfExpandedIndex, setGsfExpandedIndex] = useState(null);

  // GSF Retry/Manual Order state
  const [isRetryingGsf, setIsRetryingGsf] = useState(false);
  const [showManualOrderDialog, setShowManualOrderDialog] = useState(false);
  const [manualConfirmation, setManualConfirmation] = useState('');

  // New state for Add Service functionality
  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
  const [showAddService, setShowAddService] = useState(false);
  const [newServiceTypeId, setNewServiceTypeId] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');
  const [newServiceTax, setNewServiceTax] = useState('');
  const [newServiceLabourHours, setNewServiceLabourHours] = useState('');

  useEffect(() => {
    if (jobRequest) {
      // Show ALL items - paid and unpaid - using the is_paid field from backend
      // Initialize items for editing, sorted with paid items first
      const initialItems = jobRequest.items
        .map(item => ({
          ...item,
          price: item.price || '',
          part_number: item.part_number || '',
          labour_hours: item.labour_hours || '',
          labor_cost: item.labor_cost || 0  // For display only, calculated from labour_hours
        }))
        .sort((a, b) => (b.is_paid ? 1 : 0) - (a.is_paid ? 1 : 0));  // Paid items first
      setItems(initialItems);
      setAdminNotes(jobRequest.admin_notes || '');

      // Initialize services (for first job requests)
      if (jobRequest.services && jobRequest.services.length > 0) {
        const initialServices = jobRequest.services.map(service => {
          const price = service.price ?? '';
          const serviceName = service.service_type?.name || '';
          const isMot = serviceName.toLowerCase().includes('mot');
          // Auto-calculate VAT if not set and price exists: MOT = 0%, others = 20%
          let tax = service.tax ?? '';
          if ((tax === '' || tax === null || tax === 0) && price !== '' && price !== null) {
            const priceNum = parseFloat(price) || 0;
            tax = isMot ? 0 : (priceNum * 0.20).toFixed(2);
          }
          return {
            ...service,
            price: price,
            tax: tax,
            labour_hours: service.labour_hours ?? '',
            labor_cost: service.labor_cost ?? 0  // For display only, calculated from labour_hours
          };
        });
        setServices(initialServices);
      }
    }
  }, [jobRequest]);

  // Fetch available service types when modal opens
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const response = await api.get('/v1/service-types/');
        setAvailableServiceTypes(response.data.service_types || []);
      } catch (err) {
        console.error('Failed to fetch service types:', err);
      }
    };

    if (open) {
      fetchServiceTypes();
    }
  }, [open, jobRequest]);

  // Check if payment is completed
  const isPaymentCompleted = jobRequest?.payment?.status === 'completed';

  const handlePriceChange = (index, price) => {
    const newItems = [...items];
    newItems[index].price = price;
    setItems(newItems);
  };

  const handlePartNumberChange = (index, partNumber) => {
    const newItems = [...items];
    newItems[index].part_number = partNumber;
    setItems(newItems);
  };

  const handleLabourHoursChange = (index, labourHours) => {
    const newItems = [...items];
    newItems[index].labour_hours = labourHours;
    // Calculate labor cost client-side for preview (using technician hourly rate)
    const hourlyRate = jobRequest?.technician?.hourly_rate || 89.95;
    newItems[index].labor_cost = (parseFloat(labourHours) || 0) * hourlyRate;
    setItems(newItems);
  };

  // GSF Parts Search handlers
  const handleGSFToggle = (index) => {
    setGsfExpandedIndex(gsfExpandedIndex === index ? null : index);
  };

  const handleGSFSelect = (index, partData) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      part_number: partData.sku,
      price: partData.price.toString(),
      // GSF tracking fields for auto-ordering on payment
      gsf_sku: partData.gsf_sku,
      gsf_brand: partData.gsf_brand,
    };
    setItems(newItems);
    setGsfExpandedIndex(null);
  };

  // Helper function to check if a service is MOT (VAT-exempt)
  const isMotService = (serviceName) => {
    return serviceName?.toLowerCase().includes('mot');
  };

  const handleServicePriceChange = (index, price) => {
    const newServices = [...services];
    newServices[index].price = price;
    // Auto-calculate VAT: MOT = 0%, others = 20%
    const priceNum = parseFloat(price) || 0;
    const isMot = isMotService(newServices[index].service_type?.name);
    newServices[index].tax = isMot ? 0 : (priceNum * 0.20).toFixed(2);
    setServices(newServices);
  };

  const handleServiceTaxChange = (index, tax) => {
    const newServices = [...services];
    newServices[index].tax = tax;
    setServices(newServices);
  };

  const handleServiceLabourHoursChange = (index, labourHours) => {
    const newServices = [...services];
    newServices[index].labour_hours = labourHours;
    // Calculate labor cost client-side for preview (using technician hourly rate)
    const hourlyRate = jobRequest?.technician?.hourly_rate || 89.95;
    newServices[index].labor_cost = (parseFloat(labourHours) || 0) * hourlyRate;
    setServices(newServices);
  };

  const handleAddService = () => {
    // Validate inputs
    if (!newServiceTypeId) {
      setError('Please select a service type');
      return;
    }
    if (newServicePrice === '' || newServicePrice === null || newServicePrice === undefined || parseFloat(newServicePrice) < 0) {
      setError('Please enter a valid price (0 or greater)');
      return;
    }
    if (newServiceTax === '' || newServiceTax === null || newServiceTax === undefined || parseFloat(newServiceTax) < 0) {
      setError('Please enter a valid tax amount (0 or greater)');
      return;
    }

    // Find the selected service type
    const selectedServiceType = availableServiceTypes.find(st => st.id === parseInt(newServiceTypeId));
    if (!selectedServiceType) {
      setError('Invalid service type selected');
      return;
    }

    // Check if service already exists
    const serviceExists = services.some(s => s.service_type.id === selectedServiceType.id);
    if (serviceExists) {
      setError('This service has already been added');
      return;
    }

    // Add new service to the list
    const hourlyRate = jobRequest?.technician?.hourly_rate || 89.95;
    const labourHours = parseFloat(newServiceLabourHours || 0);
    const newService = {
      id: null, // Will be created in backend
      service_type: selectedServiceType,
      price: newServicePrice,
      tax: newServiceTax,
      labour_hours: newServiceLabourHours,
      labor_cost: labourHours * hourlyRate,
      is_new: true // Flag to indicate this is manually added
    };

    setServices([...services, newService]);

    // Reset form
    setNewServiceTypeId('');
    setNewServicePrice('');
    setNewServiceTax('');
    setNewServiceLabourHours('');
    setShowAddService(false);
    setError('');
  };

  const handleRemoveService = (index) => {
    const newServices = [...services];
    newServices.splice(index, 1);
    setServices(newServices);
  };

  const handleSubmit = async () => {
    // Validate prices (allow 0, block empty/null/negative)
    const invalidItems = items.filter(item =>
      item.price === '' || item.price === null || item.price === undefined || parseFloat(item.price) < 0
    );
    if (invalidItems.length > 0) {
      setError('Please enter valid prices for all items (0 or greater)');
      return;
    }

    // Validate services pricing (allow 0, block empty/null/negative)
    if (services.length > 0) {
      const invalidServices = services.filter(service =>
        service.price === '' || service.price === null || service.price === undefined || parseFloat(service.price) < 0 ||
        service.tax === '' || service.tax === null || service.tax === undefined || parseFloat(service.tax) < 0
      );
      if (invalidServices.length > 0) {
        setError('Please enter valid prices and tax for all services (0 or greater)');
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Save services pricing if any services exist
      if (services.length > 0) {
        const servicesPayload = services.map(service => ({
          service_type_id: service.service_type.id,
          price: parseFloat(service.price),
          tax: isMotService(service.service_type?.name) ? 0 : parseFloat(service.tax),  // MOT is VAT-exempt
          labour_hours: parseFloat(service.labour_hours || 0)
        }));
        console.log('[AdminJobRequestModal] Saving services:', servicesPayload);
        await api.post(`/v1/job-requests/${jobRequest.id}/services/`, {
          services: servicesPayload
        });
        console.log('[AdminJobRequestModal] Services saved successfully');
      }

      // Then complete the job request with items pricing
      await api.put(`/v1/job-requests/${jobRequest.id}/complete/`, {
        admin_notes: adminNotes,
        labor_cost: 0, // Deprecated - now using per-item labour hours
        items: items.map(item => ({
          id: item.id,
          price: parseFloat(item.price),
          part_number: item.part_number || '',
          labour_hours: parseFloat(item.labour_hours || 0),
          gsf_sku: item.gsf_sku || '',
          gsf_brand: item.gsf_brand || ''
        }))
      });

      setSuccess('Payment request sent to customer successfully!');
      setTimeout(() => {
        onComplete();
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete job request');
    } finally {
      setLoading(false);
    }
  };

  const handleReassignToTechnician = async () => {
    setIsReassigning(true);
    setError('');
    setSuccess('');

    try {
      // Get the technician who was assigned to this job
      const technicianId = jobRequest.technician.id;

      await api.post(`/v1/bookings/${jobRequest.booking.id}/assign/`, {
        technician_id: technicianId,
        job_request_id: jobRequest.id  // Include job_request_id to update its status
      });

      setSuccess('Job reassigned to technician successfully!');
      setTimeout(() => {
        onComplete();
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reassign job to technician');
    } finally {
      setIsReassigning(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm(`Are you sure you want to discard this parts request?`)) {
      return;
    }

    setIsDiscarding(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/v1/job-requests/${jobRequest.id}/discard/`);
      setSuccess('Job request discarded successfully!');
      setTimeout(() => {
        if (onDiscard) {
          onDiscard();
        } else {
          onComplete();
        }
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to discard job request');
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleRetryGsfOrder = async () => {
    setIsRetryingGsf(true);
    setError('');
    try {
      await gsfService.retryOrder(jobRequest.id);
      setSuccess('GSF order retry queued. Check back in a moment.');
      setTimeout(() => {
        onComplete();
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to retry GSF order');
    } finally {
      setIsRetryingGsf(false);
    }
  };

  const handleMarkManuallyOrdered = async () => {
    setError('');
    try {
      await gsfService.markManuallyOrdered(jobRequest.id, manualConfirmation);
      setSuccess('Marked as manually ordered');
      setShowManualOrderDialog(false);
      setManualConfirmation('');
      setTimeout(() => {
        onComplete();
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark as ordered');
    }
  };

  const handleClose = () => {
    setItems([]);
    setServices([]);
    setAdminNotes('');
    setError('');
    setSuccess('');
    setLoading(false);
    setIsReassigning(false);
    setIsDiscarding(false);
    setShowAddService(false);
    setNewServiceTypeId('');
    setNewServicePrice('');
    setNewServiceTax('');
    setIsRetryingGsf(false);
    setShowManualOrderDialog(false);
    setManualConfirmation('');
    onClose();
  };

  const calculatePartsTotal = () => {
    return items.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const calculateLaborTotal = () => {
    return items.reduce((total, item) => {
      const laborCost = parseFloat(item.labor_cost) || 0;
      return total + (laborCost * item.quantity);
    }, 0);
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const laborCost = parseFloat(item.labor_cost) || 0;
      return total + ((price + laborCost) * item.quantity);
    }, 0);
  };

  const calculateServicesTotal = () => {
    return services.reduce((sum, service) => {
      const price = parseFloat(service.price) || 0;
      const tax = parseFloat(service.tax) || 0;
      const laborCost = parseFloat(service.labor_cost) || 0;
      return sum + price + tax + laborCost;
    }, 0);
  };

  // Calculate services subtotal (price + labour, excluding VAT)
  const calculateServicesSubtotal = () => {
    return services.reduce((sum, service) => {
      const price = parseFloat(service.price) || 0;
      const laborCost = parseFloat(service.labor_cost) || 0;
      return sum + price + laborCost;
    }, 0);
  };

  // Calculate total VAT from services (only non-MOT services)
  const calculateServicesVat = () => {
    return services.reduce((sum, service) => {
      const tax = parseFloat(service.tax) || 0;
      return sum + tax;
    }, 0);
  };

  // Calculate VAT for parts and labour (20%)
  const calculatePartsAndLabourVat = () => {
    const partsTotal = calculatePartsTotal();
    const labourTotal = calculateLaborTotal();
    return (partsTotal + labourTotal) * 0.20;
  };

  // Calculate total VAT (services VAT + parts/labour VAT)
  const calculateTotalVat = () => {
    return calculateServicesVat() + calculatePartsAndLabourVat();
  };

  // Calculate grand total with VAT
  const calculateGrandTotal = () => {
    const servicesSubtotal = calculateServicesSubtotal();
    const servicesVat = calculateServicesVat();
    const partsTotal = calculatePartsTotal();
    const labourTotal = calculateLaborTotal();
    const partsLabourVat = calculatePartsAndLabourVat();
    return servicesSubtotal + servicesVat + partsTotal + labourTotal + partsLabourVat;
  };

  if (!jobRequest) return null;

  const { booking, technician, technician_notes } = jobRequest;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              {isPaymentCompleted ? 'Job Request - Paid' : 'Parts Request Review'}
            </Typography>
            {isPaymentCompleted && (
              <Chip
                label="PAID"
                color="success"
                icon={<CardIcon />}
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {isPaymentCompleted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Payment Completed
            </Typography>
            <Typography variant="body2">
              Customer has paid £{(jobRequest.payment.actual_amount_paid || jobRequest.payment.amount).toFixed(2)} on{' '}
              {new Date(jobRequest.payment.paid_at).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Use the "Assign Back to Technician" button below to send this job back to the technician for completion.
            </Typography>
          </Alert>
        )}

        {jobRequest.gsf_order_status === 'ordered' && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<LocalShippingIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Parts Ordered via GSF
            </Typography>
            <Typography variant="body2">
              Confirmation: {jobRequest.gsf_order_confirmation || 'Pending'}
              {jobRequest.gsf_order_eta && (
                <> | ETA: {new Date(jobRequest.gsf_order_eta).toLocaleDateString()}</>
              )}
            </Typography>
          </Alert>
        )}

        {jobRequest.gsf_order_status === 'failed' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              GSF Order Failed
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {jobRequest.gsf_order_error || 'Auto-ordering failed. You can retry or order manually.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={isRetryingGsf ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={handleRetryGsfOrder}
                disabled={isRetryingGsf}
              >
                {isRetryingGsf ? 'Retrying...' : 'Retry Order'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href="https://trade.gsfcarparts.com/"
                target="_blank"
                component="a"
              >
                Order on GSF
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                onClick={() => setShowManualOrderDialog(true)}
              >
                Mark as Ordered
              </Button>
            </Box>
          </Alert>
        )}

        {jobRequest.gsf_order_status === 'manual' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            <Typography variant="body2">
              <strong>Manually Ordered</strong>
              {jobRequest.gsf_order_confirmation && jobRequest.gsf_order_confirmation !== 'MANUAL' &&
                ` - Ref: ${jobRequest.gsf_order_confirmation}`}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Booking Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Booking Details
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Customer:</strong> {booking.customer_name}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={1}>
                <CarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Vehicle:</strong> {booking.vehicle_info}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={1}>
                <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(booking.booking_date).toLocaleDateString()} ({booking.time_of_day})
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={1}>
                <CardIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Original Cost:</strong> £{booking.total_cost}
                </Typography>
              </Box>

              {booking.work_required && booking.work_required.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Original Work Required:
                  </Typography>
                  {booking.work_required.map((work, index) => (
                    <Chip
                      key={index}
                      label={`${work.service} - £${work.cost}`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Technician Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Technician Request
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <EngineeringIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Technician:</strong> {technician.full_name} ({technician.employee_id})
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Request Notes:
              </Typography>
              <Typography variant="body2" sx={{
                fontStyle: 'italic',
                p: 1.5,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                {technician_notes}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Requested: {new Date(jobRequest.created_at).toLocaleString()}
              </Typography>

              {/* eVHC Admin Notes */}
              {jobRequest.admin_notes && (
                <Box mt={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    eVHC Admin Notes:
                  </Typography>
                  <Typography variant="body2" sx={{
                    fontStyle: 'italic',
                    p: 1.5,
                    backgroundColor: 'blue.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'blue.200',
                    color: 'blue.800'
                  }}>
                    {jobRequest.admin_notes}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Health Check Video */}
        {jobRequest.health_check_videos && jobRequest.health_check_videos.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              <VideocamIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 24 }} />
              Health Check Video
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
              {jobRequest.health_check_videos.map(video => {
                // Prepend backend URL if the file_url is relative
                const videoUrl = video.file_url.startsWith('http')
                  ? video.file_url
                  : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${video.file_url}`.replace('/api', '');

                return (
                  <Box
                    key={video.id}
                    sx={{
                      width: 300,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: '#000'
                    }}
                  >
                    <VideoPlayer
                      video={video}
                      videoUrl={videoUrl}
                    />
                    {video.caption && (
                      <Typography variant="caption" sx={{ p: 1, display: 'block', backgroundColor: '#fff' }}>
                        {video.caption}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* Health Check Images */}
        {jobRequest.health_check_images && jobRequest.health_check_images.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              <ImageIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 24 }} />
              Health Check Images
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
              {jobRequest.health_check_images.map(image => {
                // Prepend backend URL if the file_url is relative
                const imageUrl = image.file_url.startsWith('http')
                  ? image.file_url
                  : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${image.file_url}`.replace('/api', '');

                return (
                  <Box
                    key={image.id}
                    sx={{
                      width: 200,
                      height: 200,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={image.caption || 'Health check image'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(imageUrl, '_blank')}
                    />
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Services Pricing */}
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Services Pricing
              </Typography>
              {!isPaymentCompleted && !showAddService && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddService(true)}
                >
                  Add Service
                </Button>
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              {jobRequest?.is_first_request ? (
                <><strong>First Job Request:</strong> Please price the services below before sending the payment request.
                {services.length === 0 && ' Click "Add Service" to manually add services for this job.'}</>
              ) : (
                <>Add services if additional service work is needed for this job request.
                {services.length === 0 && ' Click "Add Service" to add a service.'}</>
              )}
            </Alert>

            {/* Add Service Form */}
            {showAddService && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Add New Service
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Service Type</InputLabel>
                      <Select
                        value={newServiceTypeId}
                        onChange={(e) => setNewServiceTypeId(e.target.value)}
                        label="Service Type"
                      >
                        {availableServiceTypes.map((serviceType) => (
                          <MenuItem key={serviceType.id} value={serviceType.id}>
                            {serviceType.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Price (£)"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={newServicePrice}
                      onChange={(e) => {
                        const price = e.target.value;
                        setNewServicePrice(price);
                        // Auto-calculate VAT for new service
                        const selectedService = availableServiceTypes.find(st => st.id === parseInt(newServiceTypeId));
                        const isMot = isMotService(selectedService?.name);
                        const priceNum = parseFloat(price) || 0;
                        setNewServiceTax(isMot ? '0' : (priceNum * 0.20).toFixed(2));
                      }}
                      placeholder="0.00"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="VAT (£)"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={newServiceTax}
                      onChange={(e) => setNewServiceTax(e.target.value)}
                      placeholder="0.00"
                      helperText={(() => {
                        const selectedService = availableServiceTypes.find(st => st.id === parseInt(newServiceTypeId));
                        return isMotService(selectedService?.name) ? "MOT: 0% VAT" : "20% VAT";
                      })()}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Labour Hours"
                      type="number"
                      inputProps={{ min: 0, step: 0.25 }}
                      value={newServiceLabourHours}
                      onChange={(e) => setNewServiceLabourHours(e.target.value)}
                      placeholder="0.00"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddService}
                        fullWidth
                      >
                        Add
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setShowAddService(false);
                          setNewServiceTypeId('');
                          setNewServicePrice('');
                          setNewServiceTax('');
                          setNewServiceLabourHours('');
                          setError('');
                        }}
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {services.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Service</strong></TableCell>
                      <TableCell width="120px"><strong>Price (£)</strong></TableCell>
                      <TableCell width="120px"><strong>VAT (£)</strong></TableCell>
                      <TableCell width="120px"><strong>Labour</strong></TableCell>
                      <TableCell width="120px"><strong>Total (£)</strong></TableCell>
                      {!isPaymentCompleted && <TableCell width="80px"><strong>Actions</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {services.map((service, index) => {
                      const isMot = isMotService(service.service_type?.name);
                      return (
                      <TableRow key={service.id || index}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              🔧 {service.service_type.name}
                            </Typography>
                            <Chip
                              label={isMot ? "0% VAT" : "20% VAT"}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                backgroundColor: isMot ? '#9e9e9e' : '#4caf50',
                                color: 'white',
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {isPaymentCompleted ? (
                            <Typography variant="body2" textAlign="right">
                              £{parseFloat(service.price || 0).toFixed(2)}
                            </Typography>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{
                                min: 0,
                                step: 0.01,
                                style: { textAlign: 'right' }
                              }}
                              value={service.price}
                              onChange={(e) => handleServicePriceChange(index, e.target.value)}
                              placeholder="0.00"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isPaymentCompleted ? (
                            <Typography variant="body2" textAlign="right">
                              £{parseFloat(service.tax || 0).toFixed(2)}
                            </Typography>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{
                                min: 0,
                                step: 0.01,
                                style: { textAlign: 'right' }
                              }}
                              value={service.tax}
                              onChange={(e) => handleServiceTaxChange(index, e.target.value)}
                              placeholder="0.00"
                              variant="outlined"
                              disabled={isMotService(service.service_type?.name)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isPaymentCompleted ? (
                            <Typography variant="body2" textAlign="right">
                              {parseFloat(service.labour_hours || 0).toFixed(2)}
                            </Typography>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{
                                min: 0,
                                step: 0.25,
                                style: { textAlign: 'right' }
                              }}
                              value={service.labour_hours}
                              onChange={(e) => handleServiceLabourHoursChange(index, e.target.value)}
                              placeholder="0.00"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" textAlign="right" sx={{ fontWeight: 'medium' }}>
                            £{((parseFloat(service.price) || 0) + (isMotService(service.service_type?.name) ? 0 : (parseFloat(service.tax) || 0)) + (parseFloat(service.labor_cost) || 0)).toFixed(2)}
                          </Typography>
                        </TableCell>
                        {!isPaymentCompleted && (
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveService(index)}
                              disabled={!service.is_new}
                              title={service.is_new ? 'Remove service' : 'Cannot remove auto-created service'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                    })}

                    {/* Services Total */}
                    <TableRow>
                      <TableCell colSpan={isPaymentCompleted ? 4 : 5} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          Services Total:
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" textAlign="right" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          £{services.reduce((sum, service) => sum + (parseFloat(service.price) || 0) + (isMotService(service.service_type?.name) ? 0 : (parseFloat(service.tax) || 0)) + (parseFloat(service.labor_cost) || 0), 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 3 }} />
          </>

        {/* Items Pricing */}
        <Typography variant="h6" gutterBottom>
          {isPaymentCompleted ? 'Items Status' : 'Price Parts Items'}
        </Typography>

        {/* Alert for selective payment */}
        {isPaymentCompleted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Payment Summary:</strong> Customer paid for {items.filter(i => i.is_paid).length} out of {items.length} requested items.
            Items marked in green are paid, items in red are not paid.
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Item Description</strong></TableCell>
                <TableCell width="100px"><strong>Quantity</strong></TableCell>
                <TableCell width="80px"><strong>Priority</strong></TableCell>
                <TableCell width="200px"><strong>Notes/Recommendations</strong></TableCell>
                <TableCell width="140px"><strong>Part Number</strong></TableCell>
                <TableCell width="120px"><strong>Unit Price (£)</strong></TableCell>
                <TableCell width="120px"><strong>Labour</strong></TableCell>
                <TableCell width="120px"><strong>Total (£)</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <TableRow
                  sx={{
                    backgroundColor: item.is_paid ? '#e8f5e9' : '#ffebee',
                    opacity: item.is_paid ? 1 : 0.8,
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {item.is_paid ? (
                        <Chip
                          label="✓ PAID"
                          size="small"
                          sx={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            fontWeight: 'bold',
                            height: 24,
                            fontSize: '0.7rem',
                          }}
                        />
                      ) : (
                        <Chip
                          label="NOT PAID"
                          size="small"
                          sx={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            fontWeight: 'bold',
                            height: 24,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {item.name}
                      </Typography>
                      {item.gsf_sku && (
                        <Chip
                          label="GSF"
                          size="small"
                          sx={{
                            backgroundColor: '#ed1c24',
                            color: 'white',
                            fontWeight: 'bold',
                            height: 20,
                            fontSize: '0.6rem',
                          }}
                          title="Will be auto-ordered from GSF on payment"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" textAlign="center">
                      {item.quantity}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const priorityConfig = {
                        high: { color: '#f44336', symbol: '🔴', label: 'High' },
                        medium: { color: '#ff9800', symbol: '🟡', label: 'Medium' },
                        low: { color: '#4caf50', symbol: '🟢', label: 'Low' }
                      };
                      const config = priorityConfig[item.priority] || priorityConfig.medium;
                      return (
                        <Chip
                          label={`${config.symbol} ${config.label}`}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            backgroundColor: `${config.color}20`,
                            color: config.color,
                            border: `1px solid ${config.color}40`,
                            fontWeight: 'bold'
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {item.notes && (
                      <Typography variant="body2" sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-line'
                      }}>
                        {item.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isPaymentCompleted ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {item.part_number || 'N/A'}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        value={item.part_number || ''}
                        onChange={(e) => handlePartNumberChange(index, e.target.value)}
                        placeholder="Part #"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isPaymentCompleted ? (
                      <Typography variant="body2" textAlign="right">
                        £{parseFloat(item.price || 0).toFixed(2)}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{
                          min: 0,
                          step: 0.01,
                          style: { textAlign: 'right' }
                        }}
                        value={item.price}
                        onChange={(e) => handlePriceChange(index, e.target.value)}
                        placeholder="0.00"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isPaymentCompleted ? (
                      <Typography variant="body2" textAlign="right">
                        {parseFloat(item.labour_hours || 0).toFixed(2)}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{
                          min: 0,
                          step: 0.25,
                          style: { textAlign: 'right' }
                        }}
                        value={item.labour_hours}
                        onChange={(e) => handleLabourHoursChange(index, e.target.value)}
                        placeholder="0.00"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" textAlign="right" sx={{ fontWeight: 'medium' }}>
                      £{(((parseFloat(item.price) || 0) + (parseFloat(item.labor_cost) || 0)) * item.quantity).toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {/* GSF Inline Search Row */}
                {!isPaymentCompleted && !item.is_paid && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 1, px: 2, bgcolor: '#fafafa' }}>
                      <GSFInlineSearch
                        expanded={gsfExpandedIndex === index}
                        onToggle={() => handleGSFToggle(index)}
                        onSelect={(partData) => handleGSFSelect(index, partData)}
                        vehicleRegistration={booking?.vehicle_info?.match(/\(([^)]+)\)/)?.[1] || ''}
                        itemName={item.name}
                        disabled={item.is_paid}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
              ))}

              {/* Subtotals */}
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    Parts Total:
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="right">
                    £{calculatePartsTotal().toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    Labor Total:
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="right">
                    £{calculateLaborTotal().toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                  <Typography variant="h6">
                    Total Additional Cost:
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6" textAlign="right" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    £{calculateTotal().toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* GSF Auto-Order Info */}
        {items.some(item => item.gsf_sku) && (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Items marked with <Chip label="GSF" size="small" sx={{ backgroundColor: '#ed1c24', color: 'white', fontWeight: 'bold', height: 18, fontSize: '0.6rem', mx: 0.5 }} /> will be automatically ordered from GSF when the customer completes payment.
          </Alert>
        )}

        {!isPaymentCompleted && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Admin Notes */}
            <Typography variant="h6" gutterBottom>
              Admin Notes (Optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add any notes about the pricing or additional instructions..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            {/* Summary */}
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fff8e1' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Payment Summary
              </Typography>

              {/* Subtotal Breakdown */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Subtotal (ex VAT)
                </Typography>
                {services.length > 0 ? (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                    <Typography variant="body2">Services:</Typography>
                    <Typography variant="body2">£{calculateServicesSubtotal().toFixed(2)}</Typography>
                  </Box>
                ) : !jobRequest?.is_first_request ? (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                    <Typography variant="body2" color="text.secondary">Original booking (already paid):</Typography>
                    <Typography variant="body2" color="text.secondary">£{booking.total_cost}</Typography>
                  </Box>
                ) : null}
                <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                  <Typography variant="body2">Parts:</Typography>
                  <Typography variant="body2">£{calculatePartsTotal().toFixed(2)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                  <Typography variant="body2">Labour:</Typography>
                  <Typography variant="body2">£{calculateLaborTotal().toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Subtotal:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    £{(
                      (jobRequest?.is_first_request ? calculateServicesSubtotal() : 0) +
                      calculatePartsTotal() +
                      calculateLaborTotal()
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* VAT Breakdown */}
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  VAT Breakdown
                </Typography>
                {jobRequest?.is_first_request && calculateServicesVat() > 0 && (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                    <Typography variant="body2">
                      Services VAT @ 20%:
                    </Typography>
                    <Typography variant="body2">£{calculateServicesVat().toFixed(2)}</Typography>
                  </Box>
                )}
                {(calculatePartsTotal() > 0 || calculateLaborTotal() > 0) && (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                    <Typography variant="body2">
                      Parts & Labour VAT @ 20%:
                    </Typography>
                    <Typography variant="body2">£{calculatePartsAndLabourVat().toFixed(2)}</Typography>
                  </Box>
                )}
                {/* Show MOT note if any MOT services */}
                {jobRequest?.is_first_request && services.some(s => isMotService(s.service_type?.name)) && (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      MOT @ 0% (VAT-exempt):
                    </Typography>
                    <Typography variant="body2" color="text.secondary">£0.00</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" sx={{ pl: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total VAT:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    £{(
                      (jobRequest?.is_first_request ? calculateServicesVat() : 0) +
                      calculatePartsAndLabourVat()
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Grand Total */}
              <Box sx={{ p: 1.5, backgroundColor: '#00897b', borderRadius: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'white' }}>
                    Customer Total (inc VAT):
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                    £{(jobRequest?.is_first_request
                      ? calculateGrandTotal()
                      : calculatePartsTotal() + calculateLaborTotal() + calculatePartsAndLabourVat()
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                The customer will receive an email with a secure payment link.
              </Typography>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {!isPaymentCompleted && (
            <Button
              color="error"
              onClick={handleDiscard}
              disabled={loading || isDiscarding}
              startIcon={isDiscarding ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {isDiscarding ? 'Discarding...' : 'Discard Request'}
            </Button>
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button onClick={handleClose} disabled={loading || isReassigning || isDiscarding}>
            {isPaymentCompleted ? 'Close' : 'Cancel'}
          </Button>
          {isPaymentCompleted ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleReassignToTechnician}
              disabled={isReassigning}
              startIcon={isReassigning ? <CircularProgress size={20} /> : <EngineeringIcon />}
            >
              {isReassigning ? 'Reassigning...' : 'Assign Back to Technician'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || isDiscarding || items.some(item => item.price === '' || item.price === null || item.price === undefined || parseFloat(item.price) < 0)}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Sending Payment Request...' : 'Send Payment Request to Customer'}
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Manual Order Confirmation Dialog */}
      <Dialog
        open={showManualOrderDialog}
        onClose={() => setShowManualOrderDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark as Manually Ordered</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Confirm that you have manually ordered the parts from GSF.
          </Typography>
          <TextField
            fullWidth
            label="GSF Confirmation Number (optional)"
            value={manualConfirmation}
            onChange={(e) => setManualConfirmation(e.target.value)}
            placeholder="e.g., GSF-12345"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManualOrderDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMarkManuallyOrdered}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};

// VideoPlayer component with URL refresh on error
const VideoPlayer = ({ video, videoUrl: initialUrl }) => {
  const [videoUrl, setVideoUrl] = useState(initialUrl);
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = React.useRef(null);

  const refreshVideoUrl = async () => {
    if (retryCount >= 3) {
      console.error('Max retry attempts reached for video:', video.id);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/evhc/media/${video.id}/refresh-url/`);

      if (!response.ok) {
        throw new Error('Failed to refresh video URL');
      }

      const data = await response.json();
      const newUrl = data.file_url.startsWith('http')
        ? data.file_url
        : `${apiUrl}${data.file_url}`.replace('/api', '');

      setVideoUrl(newUrl);
      setRetryCount(prev => prev + 1);

      // Reload video
      if (videoRef.current) {
        videoRef.current.load();
      }

      console.log('Video URL refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh video URL:', error);
      setRetryCount(prev => prev + 1);
    }
  };

  const handleVideoError = (e) => {
    console.error('Video playback error:', e);
    console.error('Video URL:', videoUrl);
    console.error('Error code:', videoRef.current?.error?.code);

    // Attempt to refresh URL on 403 Forbidden or network errors
    if (videoRef.current?.error?.code === 4 || videoRef.current?.error?.code === 2) {
      console.log('Attempting to refresh video URL...');
      refreshVideoUrl();
    }
  };

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      style={{ width: '100%', display: 'block' }}
      controls
      onError={handleVideoError}
    />
  );
};

export default AdminJobRequestModal;